import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { assessmentAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import AssessmentTypeFormDialog from './forms/AssessmentTypeFormDialog';
import TablePagination from '../common/TablePagination';

const extractCollection = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const extractTotal = (responseOrPayload, fallback = 0) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  return Number(payload?.total ?? payload?.data?.total ?? payload?.meta?.total ?? fallback);
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback
);

const INITIAL_FORM = {
  assessment_code: '',
  assessment_name: '',
  short_name: '',
  category: '',
  is_active: true
};

const AssessmentTypes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.ASSESSMENTS.TYPES, ACTIONS.READ);
  const canCreate = can(RESOURCES.ASSESSMENTS.TYPES, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.ASSESSMENTS.TYPES, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.ASSESSMENTS.TYPES, ACTIONS.DELETE);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const categories = ['Continuous Assessment', 'Examination', 'Project', 'Practical', 'Assignment'];

  const fetchAssessmentTypes = useCallback(async () => {
    if (!canRead) return;
    try {
      setLoading(true);
      const params = {
        page: page,
        per_page: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(activeFilter !== '' && { is_active: activeFilter })
      };
      const response = await assessmentAPI.getAssessmentTypes(params);
      const payload = response?.data ?? response;
      const rows = extractCollection(response);
      setAssessmentTypes(rows);
      setPagination({
        current_page: payload?.current_page ?? payload?.data?.current_page ?? 1,
        last_page: payload?.last_page ?? payload?.data?.last_page ?? 1,
        per_page: payload?.per_page ?? payload?.data?.per_page ?? pageSize,
        total: extractTotal(response, rows.length)
      });
    } catch (error) {
      console.error('Error fetching assessment types:', error);
      setSnackbar({ open: true, message: 'Failed to fetch assessment types', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, categoryFilter, activeFilter, canRead]);

  useEffect(() => {
    fetchAssessmentTypes();
  }, [fetchAssessmentTypes]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter, activeFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await assessmentAPI.updateAssessmentType(editingType.id, formData);
        setSnackbar({ open: true, message: 'Assessment type updated successfully', severity: 'success' });
      } else {
        await assessmentAPI.createAssessmentType(formData);
        setSnackbar({ open: true, message: 'Assessment type created successfully', severity: 'success' });
      }
      fetchAssessmentTypes();
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      assessment_code: type.assessment_code || '',
      assessment_name: type.assessment_name || '',
      short_name: type.short_name || '',
      category: type.category || '',
      is_active: Boolean(type.is_active)
    });
    setShowForm(true);
  };

  const handleDelete = (id) => setConfirmDelete(id);

  const confirmDeleteAction = async () => {
    try {
      await assessmentAPI.deleteAssessmentType(confirmDelete);
      setSnackbar({ open: true, message: 'Assessment type deleted successfully', severity: 'success' });
      fetchAssessmentTypes();
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to delete assessment type'), severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setEditingType(null);
    setShowForm(false);
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view assessment types.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Assessment Types"
        subtitle="Manage assessment types and categories"
        actions={canCreate ? [
          <Button
            key="add"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
            fullWidth={isMobile}
          >
            Add Assessment Type
          </Button>
        ] : []}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={categories}
                value={categoryFilter || null}
                onChange={(_, value) => setCategoryFilter(value || '')}
                renderInput={(params) => <TextField {...params} label="Category" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={[
                  { label: 'Active', value: true },
                  { label: 'Inactive', value: false }
                ]}
                value={activeFilter === '' ? null : { label: activeFilter ? 'Active' : 'Inactive', value: activeFilter }}
                onChange={(_, option) => setActiveFilter(option ? option.value : '')}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : assessmentTypes.length === 0 ? (
            <EmptyRecordsState message="No assessment types found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={assessmentTypes}
              renderItem={(type) => (
                <Stack spacing={1.25}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700}>{type.assessment_name}</Typography>
                    <Chip
                      label={type.is_active ? 'Active' : 'Inactive'}
                      color={type.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {type.assessment_code} | {type.short_name}
                  </Typography>
                  <Chip label={type.category} size="small" sx={{ width: 'fit-content' }} />
                  <Box textAlign="right">
                    {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(type)}><EditIcon /></IconButton>}
                    {canDelete && <IconButton size="small" color="error" onClick={() => handleDelete(type.id)}><DeleteIcon /></IconButton>}
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Short Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentTypes.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell><strong>{type.assessment_code}</strong></TableCell>
                      <TableCell>{type.assessment_name}</TableCell>
                      <TableCell>{type.short_name}</TableCell>
                      <TableCell><Chip label={type.category} size="small" /></TableCell>
                      <TableCell>
                        <Chip label={type.is_active ? 'Active' : 'Inactive'} color={type.is_active ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(type)}><EditIcon /></IconButton>}
                        {canDelete && <IconButton size="small" color="error" onClick={() => handleDelete(type.id)}><DeleteIcon /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => { setPageSize(parseInt(size)); setPage(1); }}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Assessment Type</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this assessment type? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <AssessmentTypeFormDialog
        open={showForm}
        onClose={resetForm}
        onSubmit={handleSubmit}
        editingType={editingType}
        formData={formData}
        setFormData={setFormData}
        categories={categories}
        isMobile={isMobile}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessmentTypes;
